/**
 * Rax Data Export Component
 *
 * Allows users to export their data (conversations, audit logs, learning ledger).
 * Provides format options (JSON, CSV) and triggers downloads.
 * Also includes GDPR-compliant data deletion option.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { raxApi } from "../../api/rax-api.js";
import { icons } from "../icons.js";
import { raxTheme } from "../styles/rax-theme.js";
// Import the deletion component
import "./data-deletion.js";

type ExportFormat = "json" | "csv" | "jsonl";
type ExportType = "audit" | "ledger" | "all";

interface ExportOption {
  id: ExportType;
  label: string;
  description: string;
  icon: string;
  formats: ExportFormat[];
}

@customElement("rax-data-export")
export class RaxDataExport extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .export-container {
        padding: var(--space-6);
        max-width: 800px;
      }
      
      .export-header {
        margin-bottom: var(--space-6);
      }
      
      .export-title {
        font-size: var(--font-xl);
        font-weight: 700;
        color: var(--text-primary);
        margin-bottom: var(--space-2);
      }
      
      .export-description {
        color: var(--text-secondary);
        font-size: var(--font-sm);
        line-height: 1.5;
      }
      
      /* Export Options Grid */
      .export-options {
        display: grid;
        gap: var(--space-4);
        margin-bottom: var(--space-6);
      }
      
      .export-option {
        display: flex;
        align-items: flex-start;
        gap: var(--space-4);
        padding: var(--space-5);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        transition: all var(--transition-fast);
      }
      
      .export-option:hover {
        border-color: var(--rax-yellow);
        background: rgba(255, 214, 0, 0.03);
      }
      
      .export-option.selected {
        border-color: var(--rax-yellow);
        background: rgba(255, 214, 0, 0.08);
      }
      
      .option-checkbox {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        border: 2px solid var(--border-color);
        border-radius: var(--radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all var(--transition-fast);
        margin-top: 2px;
      }
      
      .option-checkbox:hover {
        border-color: var(--rax-yellow);
      }
      
      .option-checkbox.checked {
        background: var(--rax-yellow);
        border-color: var(--rax-yellow);
      }
      
      .option-checkbox svg {
        width: 14px;
        height: 14px;
        stroke: var(--rax-black);
        opacity: 0;
        transition: opacity var(--transition-fast);
      }
      
      .option-checkbox.checked svg {
        opacity: 1;
      }
      
      .option-icon {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--bg-secondary);
        border-radius: var(--radius-md);
        font-size: 20px;
      }
      
      .option-content {
        flex: 1;
        min-width: 0;
      }
      
      .option-label {
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-1);
      }
      
      .option-desc {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        line-height: 1.4;
      }
      
      /* Format Selection */
      .format-section {
        margin-bottom: var(--space-6);
      }
      
      .section-label {
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-3);
        font-size: var(--font-sm);
      }
      
      .format-options {
        display: flex;
        gap: var(--space-2);
        flex-wrap: wrap;
      }
      
      .format-btn {
        padding: var(--space-2) var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        font-size: var(--font-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .format-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--text-primary);
      }
      
      .format-btn.active {
        background: var(--rax-yellow);
        border-color: var(--rax-yellow);
        color: var(--rax-black);
        font-weight: 600;
      }
      
      .format-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      /* Export Button */
      .export-actions {
        display: flex;
        gap: var(--space-3);
        align-items: center;
      }
      
      .export-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-6);
        background: var(--rax-yellow);
        color: var(--rax-black);
        border: none;
        border-radius: var(--radius-md);
        font-weight: 600;
        font-size: var(--font-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .export-btn:hover:not(:disabled) {
        background: var(--rax-gold);
        box-shadow: var(--shadow-glow);
      }
      
      .export-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .export-btn svg {
        width: 16px;
        height: 16px;
        stroke: currentColor;
        fill: none;
      }
      
      /* Status Messages */
      .status-message {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--font-sm);
      }
      
      .status-message.success {
        color: var(--success);
      }
      
      .status-message.error {
        color: var(--error);
      }
      
      .status-message.loading {
        color: var(--text-secondary);
      }
      
      .spinner {
        width: 16px;
        height: 16px;
        border: 2px solid var(--border-color);
        border-top-color: var(--rax-yellow);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
      
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
      
      /* Info Box */
      .info-box {
        margin-top: var(--space-6);
        padding: var(--space-4);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
      }
      
      .info-box-title {
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-2);
        font-size: var(--font-sm);
      }
      
      .info-box-content {
        font-size: var(--font-xs);
        color: var(--text-secondary);
        line-height: 1.5;
      }
      
      .info-box-content ul {
        margin: var(--space-2) 0 0 0;
        padding-left: var(--space-4);
      }
      
      .info-box-content li {
        margin-bottom: var(--space-1);
      }
      
      /* Section Divider */
      .section-divider {
        margin: var(--space-8) 0;
        border: none;
        border-top: 1px solid var(--border-color);
      }
      
      /* Danger Zone */
      .danger-zone {
        margin-top: var(--space-6);
      }
      
      .danger-zone-header {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-4);
        background: rgba(239, 68, 68, 0.05);
        border: 1px solid rgba(239, 68, 68, 0.2);
        border-bottom: none;
        border-radius: var(--radius-md) var(--radius-md) 0 0;
      }
      
      .danger-zone-header svg {
        width: 18px;
        height: 18px;
        stroke: var(--error);
        fill: none;
        stroke-width: 1.8px;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
      
      .danger-zone-title {
        font-weight: 600;
        color: var(--error);
        font-size: var(--font-sm);
      }
      
      .danger-zone-content {
        border: 1px solid rgba(239, 68, 68, 0.2);
        border-top: none;
        border-radius: 0 0 var(--radius-md) var(--radius-md);
        background: var(--bg-secondary);
      }
    `,
  ];

  @state()
  private selectedTypes: Set<ExportType> = new Set(["audit", "ledger"]);

  @state()
  private selectedFormat: ExportFormat = "json";

  @state()
  private isExporting = false;

  @state()
  private exportStatus: { type: "success" | "error" | "loading"; message: string } | null = null;

  private exportOptions: ExportOption[] = [
    {
      id: "audit",
      label: "Activity Log",
      description:
        "Your complete audit trail including all agent actions, tool calls, and system events.",
      icon: "📋",
      formats: ["json", "csv", "jsonl"],
    },
    {
      id: "ledger",
      label: "Learning Ledger",
      description:
        "All learned preferences, corrections, and personalization data the agent has accumulated.",
      icon: "🧠",
      formats: ["json"],
    },
    {
      id: "all",
      label: "All Data (ZIP)",
      description:
        "Complete data export including activity logs, learning ledger, preferences, and agent configurations.",
      icon: "📦",
      formats: ["json"],
    },
  ];

  private toggleSelection(type: ExportType) {
    const newSet = new Set(this.selectedTypes);

    // If selecting "all", clear others
    if (type === "all") {
      newSet.clear();
      newSet.add("all");
    } else {
      // If selecting individual types, remove "all"
      newSet.delete("all");
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
    }

    this.selectedTypes = newSet;

    // Reset format to json if current format not supported
    const availableFormats = this.getAvailableFormats();
    if (!availableFormats.includes(this.selectedFormat)) {
      this.selectedFormat = "json";
    }
  }

  private getAvailableFormats(): ExportFormat[] {
    if (this.selectedTypes.has("all")) {
      return ["json"];
    }

    // Return intersection of formats supported by all selected types
    const formatSets = Array.from(this.selectedTypes).map(
      (type) => new Set(this.exportOptions.find((o) => o.id === type)?.formats || []),
    );

    if (formatSets.length === 0) return ["json", "csv", "jsonl"];

    const result: ExportFormat[] = [];
    for (const format of ["json", "csv", "jsonl"] as ExportFormat[]) {
      if (formatSets.every((set) => set.has(format))) {
        result.push(format);
      }
    }

    return result.length > 0 ? result : ["json"];
  }

  private async handleExport() {
    if (this.selectedTypes.size === 0) {
      this.exportStatus = {
        type: "error",
        message: "Please select at least one data type to export.",
      };
      return;
    }

    this.isExporting = true;
    this.exportStatus = { type: "loading", message: "Preparing your export..." };

    try {
      if (this.selectedTypes.has("all")) {
        // Export all data as ZIP
        const blob = await raxApi.exportAllData();
        this.downloadBlob(blob, `rax-data-export-${this.getDateStamp()}.zip`);
        this.exportStatus = { type: "success", message: "All data exported successfully!" };
      } else {
        // Export individual types
        const exports: { name: string; blob: Blob }[] = [];

        if (this.selectedTypes.has("audit")) {
          const blob = await raxApi.exportAuditLogs(this.selectedFormat);
          const ext = this.selectedFormat === "jsonl" ? "jsonl" : this.selectedFormat;
          exports.push({ name: `rax-audit-${this.getDateStamp()}.${ext}`, blob });
        }

        if (this.selectedTypes.has("ledger")) {
          const blob = await raxApi.exportLedgerData();
          exports.push({ name: `rax-ledger-${this.getDateStamp()}.json`, blob });
        }

        // Download each file
        for (const exp of exports) {
          this.downloadBlob(exp.blob, exp.name);
          // Small delay between downloads to prevent browser blocking
          await new Promise((r) => setTimeout(r, 100));
        }

        this.exportStatus = {
          type: "success",
          message: `${exports.length} file${exports.length > 1 ? "s" : ""} exported successfully!`,
        };
      }
    } catch (error) {
      console.error("[DataExport] Export failed:", error);
      this.exportStatus = {
        type: "error",
        message: error instanceof Error ? error.message : "Export failed. Please try again.",
      };
    } finally {
      this.isExporting = false;

      // Clear success message after a few seconds
      if (this.exportStatus?.type === "success") {
        setTimeout(() => {
          this.exportStatus = null;
        }, 5000);
      }
    }
  }

  private downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private getDateStamp(): string {
    return new Date().toISOString().slice(0, 10);
  }

  override render() {
    const availableFormats = this.getAvailableFormats();

    return html`
      <div class="export-container">
        <div class="export-header">
          <h2 class="export-title">Export Your Data</h2>
          <p class="export-description">
            Download a copy of your data from Rax. Select what you want to export and your preferred format.
            Your data belongs to you.
          </p>
        </div>

        <div class="export-options">
          ${this.exportOptions.map(
            (option) => html`
            <div
              class="export-option ${this.selectedTypes.has(option.id) ? "selected" : ""}"
              @click=${() => this.toggleSelection(option.id)}
            >
              <div class="option-checkbox ${this.selectedTypes.has(option.id) ? "checked" : ""}">
                ${icons.check}
              </div>
              <div class="option-icon">${option.icon}</div>
              <div class="option-content">
                <div class="option-label">${option.label}</div>
                <div class="option-desc">${option.description}</div>
              </div>
            </div>
          `,
          )}
        </div>

        ${
          !this.selectedTypes.has("all")
            ? html`
          <div class="format-section">
            <div class="section-label">Export Format</div>
            <div class="format-options">
              ${(["json", "csv", "jsonl"] as ExportFormat[]).map(
                (format) => html`
                <button
                  class="format-btn ${this.selectedFormat === format ? "active" : ""}"
                  ?disabled=${!availableFormats.includes(format)}
                  @click=${() => (this.selectedFormat = format)}
                >
                  ${format.toUpperCase()}
                </button>
              `,
              )}
            </div>
          </div>
        `
            : ""
        }

        <div class="export-actions">
          <button
            class="export-btn"
            ?disabled=${this.isExporting || this.selectedTypes.size === 0}
            @click=${this.handleExport}
          >
            ${
              this.isExporting
                ? html`
                    <div class="spinner"></div>
                    Exporting...
                  `
                : html`
              ${icons.download}
              Export Selected
            `
            }
          </button>

          ${
            this.exportStatus
              ? html`
            <div class="status-message ${this.exportStatus.type}">
              ${
                this.exportStatus.type === "loading"
                  ? html`
                      <div class="spinner"></div>
                    `
                  : ""
              }
              ${this.exportStatus.type === "success" ? "✓" : ""}
              ${this.exportStatus.type === "error" ? "✗" : ""}
              ${this.exportStatus.message}
            </div>
          `
              : ""
          }
        </div>

        <div class="info-box">
          <div class="info-box-title">About Your Data Export</div>
          <div class="info-box-content">
            <ul>
              <li><strong>Activity Log:</strong> Contains timestamps, actions taken, tool calls, and costs.</li>
              <li><strong>Learning Ledger:</strong> Includes preferences, corrections, and personalization data.</li>
              <li><strong>All Data:</strong> A complete ZIP archive with all exportable data plus agent configurations.</li>
            </ul>
            Exports are generated on-demand and reflect the current state of your data.
          </div>
        </div>

        <hr class="section-divider" />

        <!-- GDPR Data Deletion Section -->
        <div class="danger-zone">
          <div class="danger-zone-header">
            ${icons.alertTriangle}
            <span class="danger-zone-title">Danger Zone</span>
          </div>
          <div class="danger-zone-content">
            <rax-data-deletion></rax-data-deletion>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-data-export": RaxDataExport;
  }
}
